import React, { useState, useEffect } from 'react';
import './HomePage.css';
import './SocialLinks.css';
import SEO from '../components/SEO';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../supabase';
import logo from '../assets/pokeshani-logo.png';
import proof1 from '../assets/proof1.png';
import FeeCalculator from './FeeCalculator';

// proof1 is used as the hero dashboard screenshot.
// proof2.png remains in src/assets if you want to reuse it elsewhere.

const calculateRevenue = (price: number, sales: number) => {
  const gumroadRevenue = price * sales * 0.9; // Gumroad takes a 10% flat fee
  const pokeShaniRevenue = price * sales; // PokeShani takes 0%
  return pokeShaniRevenue - gumroadRevenue;
};
interface Product {
  id: string;
  title: string;
  thumbnail: string;
  slug: string;
  price: number;
  created_at: string;
  category: string;
  is_deleted: boolean;
}

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [calculatedRevenue, setCalculatedRevenue] = useState<number>(0);
  const [salePrice, setSalePrice] = useState<number>(10);
  const [monthlySales, setMonthlySales] = useState<number>(100);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState<boolean>(false);


  useEffect(() => {
    setIsVisible(true);
    fetchFeaturedProducts();
  }, []);
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  
  const fetchFeaturedProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('digitalproducts')
        .select('id, title, thumbnail, slug, price, created_at, category, is_deleted')
        .eq('is_deleted', false) // Only fetch non-deleted products
        .order('created_at', { ascending: false })
        .limit(3); // Changed from 4 to 3 to show only 3 products on homepage

      if (error) {
        console.error('Error fetching featured products:', error);
        return;
      }

      // Double-check that no deleted products are included (extra safety)
      const nonDeletedProducts = (data || []).filter(product => product.is_deleted === false || product.is_deleted === undefined);
      
      setFeaturedProducts(nonDeletedProducts);
      setLoading(false);
    } catch (err) {
      console.error('Error:', err);
      setLoading(false);
    }
  };

  return (
    <>
      <SEO />
      <motion.div 
        className="home-container"
        initial={{ opacity: 0 }}
        animate={{ opacity: isVisible ? 1 : 0 }}
        transition={{ duration: 0.8 }}
      >
      <header className="site-header">
        <div className="logo-container">
          <img src={logo} alt="PokeShani Logo" className="header-logo" />
          <span className="site-name">PokeShani</span>
        </div>
        <button
          className={`mobile-menu-toggle ${isMobileNavOpen ? 'open' : ''}`}
          onClick={() => setIsMobileNavOpen((prev) => !prev)}
          aria-label="Toggle navigation"
        >
          <span />
          <span />
          <span />
        </button>
        <div className={`header-nav ${isMobileNavOpen ? 'open' : ''}`}>
          <Link to="/marketplace" className="nav-link">Discover</Link>
          <Link to="/pricing" className="nav-link">Pricing</Link>
          <Link to="/faq" className="nav-link">Help</Link>
          <Link to="/login" className="nav-link">Login</Link>
          <Link to="/signup" className="nav-link cta-nav-link">Signup</Link>
        </div>
      </header>

      <motion.section 
        className="hero-section"
      >
        <motion.div 
          className="hero-content"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="hero-text">
            <h1 className="hero-title">
              Sell Digital Products. Keep 100%.
            </h1>
            <p className="hero-subtitle">
              You get paid within 5 seconds of making any sale, no threshold.
              <br className="mobile-break" />
              And you can sell anything that&apos;s a link or URL.
            </p>
            <motion.button
              className="cta-button"
              onClick={() => navigate('/signup')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Get Paid Now
            </motion.button>
          </div>
          <motion.div
            className="hero-visual"
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.45 }}
          >
            <div className="hero-visual-frame">
              <img
                src={proof1}
                alt="PokeShani creator dashboard showing real sales, views and PayPal payouts"
                className="hero-visual-image"
                decoding="async"
              />
            </div>
            <div className="hero-visual-badge">
              <span className="hero-visual-badge-icon" aria-hidden="true">⚡</span>
              Paid in 5 seconds
            </div>
          </motion.div>
        </motion.div>
      </motion.section>

      {/* Live fee calculator — replaces the proof grid (proof1 already shown in the hero) */}
      <FeeCalculator />

      {/* Featured products — moved directly under the payment proofs */}
      <motion.section 
        className="discover-section"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <motion.h2 
          className="section-title"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          What creators are selling on PokeShani
        </motion.h2>
        <motion.div 
          className="products-section"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
        >
          <div className="products-row">
            {loading ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
              </div>
            ) : featuredProducts.length > 0 ? (
              featuredProducts.map((product) => (
                <div key={product.id} className="product-card">
                  {product.thumbnail ? (
                    <img
                      src={product.thumbnail}
                      alt={product.title}
                      className="product-thumbnail"
                      loading="lazy"
                    />
                  ) : (
                    <div className="placeholder-image">No Image</div>
                  )}
                  <div className="product-info">
                    <h3 className="product-title">{product.title}</h3>
                    <div className="product-price">${product.price.toFixed(2)}</div>
                    <div className="product-actions">
                      <Link to={`/sales/${product.slug}`} className="buy-now-btn">View product</Link>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-products">No products available</div>
            )}
          </div>
          <div className="view-all-container">
            <Link to="/marketplace" className="discover-small-button">Discover more Products</Link>
          </div>
        </motion.div>
      </motion.section>

      <motion.section 
        className="steps-section"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <div className="steps-container">
          <h2 className="section-title">How It Works</h2>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-icon">🎨</div>
              <h3 className="step-title">Make a Sales Page</h3>
              <p className="step-description">
                Create a clean page for your product. Add price and details.
                Publish.
              </p>
            </div>

            <div className="step-card">
              <div className="step-icon">🔗</div>
              <h3 className="step-title">Connect Your Product</h3>
              <p className="step-description">
                Paste your access link from Udemy, Teachable, Google Drive,
                Notion, or anywhere else. Your product stays where it is.
              </p>
            </div>

            <div className="step-card">
              <div className="step-icon">💰</div>
              <h3 className="step-title">Start Selling</h3>
              <p className="step-description">
                Share your page. Buyers pay with PayPal and get instant access.
                They also get a receipt by email automatically. You get paid
                right away.
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      <motion.section 
        className="youtube-video-section"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <div className="youtube-video-container">
          <h2 className="section-title">How to use PokeShani</h2>
          <div className="video-content-wrapper">
            <div className="video-content-left">
              <p className="section-subtitle">
                Watch this quick walkthrough to see exactly how to set up your product,
                connect your PayPal, and start selling on PokeShani.
              </p>
            </div>
            <div className="video-content-right">
              <div className="video-wrapper">
                <iframe
                  src="https://www.youtube.com/embed/USbyMWDrffw"
                  title="How to use PokeShani"
                  frameBorder="0"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      <motion.section 
        className="features-section"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <motion.div 
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="features-grid">
          <h2 className="section-title">Why PokeShani</h2>
          <p className="section-subtitle">
            Other platforms take a cut or trap you in. PokeShani doesn&apos;t.
            Your product stays where it already is. We handle payment, access,
            and automatically send a receipt to your buyer.
          </p>
        </motion.div>
      </motion.section>

      {/* Who PokeShani Is For & How It Works section removed per request — redundant with How It Works steps above */}

      {/* Why Creators Like PokeShani section removed per request */}

      {/* Safe & Simple section removed per request */}

      {/* Success story section moved to the top of the page */}

      <motion.section 
        className="getting-started-section"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <h2 className="section-title">Pricing</h2>
        <div className="getting-started-content">
          <p className="creators-info">
            PokeShani is planning to add optional premium features in future,
            but we don&apos;t take a cut from your sales.
          </p>
          <h3 className="earnings-title">Get Started</h3>
          <p className="creators-info">
            Start selling your digital products today. No fees. Leave whenever
            you want.
          </p>
          <motion.button
            className="cta-button"
            onClick={() => navigate('/signup')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Start Selling
          </motion.button>
        </div>

        <div className="stats-container">
          <div className="stat-item">
            <h3>0% Fees</h3>
            <p>KEEP ALL YOUR EARNINGS</p>
          </div>
          <div className="stat-item">
            <h3>Easy Setup</h3>
            <p>START IN MINUTES</p>
          </div>
        </div>
      </motion.section>



      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            
            <div className="social-links">
              <a href="https://www.instagram.com/pokeshaniofficial" target="_blank" rel="noopener noreferrer" className="social-link instagram">
                <svg viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a href="https://x.com/PokeShani" target="_blank" rel="noopener noreferrer" className="social-link">
                <svg viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
{/* Links to the static SEO content pages. These MUST be plain <a> tags,
            not react-router <Link>, because those pages are standalone static
            HTML files generated at build time and are not React routes. */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '1.25rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          <a href="/creator-fee-calculator" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', fontSize: '0.9rem' }}>Fee calculator</a>
          <a href="/gumroad-alternative" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', fontSize: '0.9rem' }}>Gumroad alternative</a>
          <a href="/lemon-squeezy-alternative" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', fontSize: '0.9rem' }}>Lemon Squeezy alternative</a>
          <a href="/payhip-alternative" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', fontSize: '0.9rem' }}>Payhip alternative</a>
          <a href="/sellfy-alternative" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', fontSize: '0.9rem' }}>Sellfy alternative</a>
          <a href="/whop-alternative" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', fontSize: '0.9rem' }}>Whop alternative</a>
        </div>
        <div className="footer-bottom">
          <p>© {new Date().getFullYear()} PokeShani All rights reserved.</p>
          <p>0% Fees · Instant PayPal Payments · Leave Anytime</p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', marginTop: '1rem', flexWrap: 'wrap' }}>
            <Link 
              to="/privacy-policy" 
              style={{ 
                color: 'rgba(255, 255, 255, 0.7)', 
                textDecoration: 'none',
                fontSize: '0.9rem',
                transition: 'color 0.3s ease'
              }}
              onMouseOver={(e) => e.currentTarget.style.color = '#ffffff'}
              onMouseOut={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)'}
            >
              Privacy Policy
            </Link>
            <Link 
              to="/terms-of-service" 
              style={{ 
                color: 'rgba(255, 255, 255, 0.7)', 
                textDecoration: 'none',
                fontSize: '0.9rem',
                transition: 'color 0.3s ease'
              }}
              onMouseOver={(e) => e.currentTarget.style.color = '#ffffff'}
              onMouseOut={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)'}
            >
              Terms of Service
            </Link>
            <Link 
              to="/faq" 
              style={{ 
                color: 'rgba(255, 255, 255, 0.7)', 
                textDecoration: 'none',
                fontSize: '0.9rem',
                transition: 'color 0.3s ease'
              }}
              onMouseOver={(e) => e.currentTarget.style.color = '#ffffff'}
              onMouseOut={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)'}
            >
              FAQ
            </Link>
            <Link 
              to="/roadmap" 
              style={{ 
                color: 'rgba(255, 255, 255, 0.7)', 
                textDecoration: 'none',
                fontSize: '0.9rem',
                transition: 'color 0.3s ease'
              }}
              onMouseOver={(e) => e.currentTarget.style.color = '#ffffff'}
              onMouseOut={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)'}
            >
              Updates
            </Link>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1rem', flexWrap: 'wrap' }}>
            <a href="https://launchigniter.com/product/pokeshani?ref=badge-pokeshani" target="_blank" rel="noopener noreferrer">
              <img 
                src="https://launchigniter.com/api/badge/pokeshani?theme=dark" 
                alt="Featured on LaunchIgniter" 
                loading="lazy"
                width="212" 
                height="55" 
                style={{ display: 'block' }}
              />
            </a>
            <a href="https://awesomeindie.com/product/poke-shani?ref=badge" target="_blank" rel="noopener">
              <img 
                src="https://awesomeindie.com/images/badges/awesome-indie-featured-dark.svg" 
                alt="PokeShani — Featured on Awesome Indie" 
                width="184" 
                height="54" 
                style={{ display: 'block' }}
              />
            </a>
            <a href="https://indiechains.com" target="_blank" rel="noopener noreferrer">
              <img
                src="https://api.indiechains.com/badge/d0dd27bb1ab7cccfccce56b087f5d3d843f8bfcadd4cf54d.svg?theme=dark"
                alt="Linked with Indie Chains"
                width="180"
                height="40"
                style={{ display: 'block' }}
              />
            </a>
          </div>
        </div>
      </footer>
    </motion.div>
    </>
  );
}

export default HomePage;
